// The doubt card (owner decision 2026-10-16, 2a): what
// Loom asks the owner about. Owner only: the link page never renders it, and
// its DTO cannot carry it (`MeetingLinkReviewDto` is an allowlist).
//
// - One question per entry, as the server grouped them: "Ein Thema oder
//   zwei?" names the pair, a reference outside the meeting names every item
//   pointing at the same kind of source, the rest one item each. The first
//   `MEETING_MAX_DOUBTS` show; the rest wait behind "N weitere Rückfragen
//   anzeigen" and are answerable once shown. A server that sent only the
//   first five says how many more wait under "Nicht übernommen" instead.
// - Each item shows its label, when it was said, what Loom would have made
//   of it, and its quotes.
// - A held item is usually already an issue, filed as Loom's guess
//   (owner decision 2026-10-29): it says "Angelegt als #N". Its answers
//   change that issue: a skip reads
//   "Schliessen" (except "Nur eine Idee"), leaving one out "Dieses
//   schliessen", "Ein Thema" merges ("Ein Thema, zusammenführen").
// - The answers are the question's own (the server's list); Loom's guess,
//   when it has one, comes first as the main button. An answer files or
//   changes the issues on the server; the page then reloads the meeting.
// - A question about several items that files each on its own ("Ja,
//   übernehmen", "Zwei Themen") lets the owner leave single items out
//   ("Dieses nicht anlegen"): those are not filed. With an item left out,
//   "Ein Thema" is off (it needs both), and leaving every item out leaves
//   only "Nichts anlegen".
// - A question an earlier evaluation asked, carried to this one by its
//   subject ("Neu verarbeiten"), says "Schon früher gefragt"; one whose
//   earlier answer the new evaluation does not clearly match is asked again
//   with that answer shown. A carried "Jetzt Arbeit oder nur eine Idee?" a
//   rule settled is listed as decided ("Im Meeting entschieden", why), with
//   no buttons.
// - Nothing here writes to the project but what an answer files or changes.

import { useState } from "react";

import {
  MEETING_MAX_DOUBTS,
  TOPIC_REASONS,
  meetingClock,
  type MeetingDoubtAnswer,
  type MeetingDoubtDto,
  type MeetingDoubtItemDto,
  type MeetingSettledDoubtDto,
} from "@loom/shared/meetings";

import { usePlural, useT, type MessageKey } from "../../i18n/index.js";
import { Help } from "../ui";

import { QuoteList } from "./MeetingReview";

/** The question's line: by its doubt, and for a reference by where it points. */
export const doubtQuestionKeyOf = (d: Pick<MeetingDoubtDto, "code" | "ref">): MessageKey =>
  (d.code === "unresolved_ref"
    ? `meetings.doubts.q.unresolved_ref.${d.ref === "earlier_meeting" || d.ref === "external_doc" ? d.ref : "none"}`
    : `meetings.doubts.q.${d.code}`) as MessageKey;

/** Loom's guess first, then the rest in the server's order. */
export const orderedAnswers = (d: Pick<MeetingDoubtDto, "answers" | "suggested">): MeetingDoubtAnswer[] =>
  d.suggested !== null && d.answers.includes(d.suggested)
    ? [d.suggested, ...d.answers.filter((a) => a !== d.suggested)]
    : [...d.answers];

/** May the owner leave single items out? Only of a question about several that files each on its own. */
export const canLeaveOut = (d: Pick<MeetingDoubtDto, "items" | "answers">): boolean =>
  d.items.length >= 2 && d.answers.includes("as_held");

/** Is this item already an issue (filed as Loom's guess while the question waits)? */
export const isFiledItem = (i: Pick<MeetingDoubtItemDto, "filedIid">): boolean =>
  (i.filedIid ?? null) !== null;

/**
 * An answer's label. On a question with a filed item the answer changes that
 * issue: a skip closes it (but "Nur eine Idee" keeps it, as an idea), and
 * "Ein Thema" merges.
 */
export const doubtAnswerKeyOf = (
  d: Pick<MeetingDoubtDto, "code" | "items">,
  a: MeetingDoubtAnswer,
): MessageKey => {
  const filed = d.items.some(isFiledItem);
  if (filed && a === "skip" && d.code !== "work_or_idea") return "meetings.doubts.a.skip.filed";
  if (filed && a === "merged") return "meetings.doubts.a.same_or_two.merged.filed";
  return `meetings.doubts.a.${d.code}.${a}` as MessageKey;
};

/** Is answer `a` open with `leftOut` of the question's `items` left out? */
export const answerOpen = (a: MeetingDoubtAnswer, leftOut: number, items: number): boolean =>
  leftOut === 0 || a === "skip" || (a === "as_held" && leftOut < items);

const isTopicReason = (r: string): boolean => (TOPIC_REASONS as readonly string[]).includes(r);

function HeldAs({ item }: { readonly item: MeetingDoubtItemDto }) {
  const t = useT();
  if (item.heldClass === null) return null;
  let what: string | null = null;
  if (item.heldClass === "none") {
    // Context only ("chatter") says nothing the question does not: the card
    // asks whether to file it, so "Nebengespräch" would contradict it.
    what =
      item.heldReason !== null &&
      isTopicReason(item.heldReason) &&
      item.heldReason !== "doubt" &&
      item.heldReason !== "chatter"
        ? t(`meetings.notTaken.reason.${item.heldReason}` as MessageKey)
        : null;
  } else {
    const cls = t(`meetings.class.${item.heldClass}` as MessageKey);
    what =
      item.heldReason !== null && !isTopicReason(item.heldReason)
        ? `${cls} · ${t(`meetings.reason.${item.heldReason}` as MessageKey)}`
        : cls;
  }
  if (what === null) return null;
  return <span className="meet-meta">{t("meetings.doubts.heldAs", { what })}</span>;
}

/** "Angelegt als #N", linked to the issue when the server sent where it is. */
function FiledAs({ item }: { readonly item: MeetingDoubtItemDto }) {
  const t = useT();
  const iid = item.filedIid ?? null;
  if (iid === null) return null;
  const label = t("meetings.doubts.filedAs", { iid });
  const url = item.filedUrl ?? null;
  return (
    <p className="meet-meta">
      {url === null ? (
        label
      ) : (
        <a className="focus-deeplink" href={url} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      )}
    </p>
  );
}

/** Carried doubts a rule settled: decided, never asked. */
function SettledDoubts({ settled }: { readonly settled: readonly MeetingSettledDoubtDto[] }) {
  const t = useT();
  if (settled.length === 0) return null;
  return (
    <div className="meet-doubts-settled" data-settled>
      <p className="meet-question-text">{t("meetings.doubts.settled.title")}</p>
      <ul className="meet-list">
        {settled.map((d) => (
          <li key={d.topicId} className="meet-row" data-settled-doubt={d.topicId}>
            <div className="meet-row-head">
              {d.summary !== null && <span className="meet-summary">{d.summary}</span>}
              <span className="meet-meta">{t("meetings.quote.at", { clock: meetingClock(d.startMs) })}</span>
            </div>
            <p className="meet-meta">{t(`meetings.doubts.settled.${d.rule}` as MessageKey)}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function MeetingDoubts({
  doubts,
  pastCap,
  settled = [],
  busyDoubtId,
  onAnswer,
}: {
  readonly doubts: readonly MeetingDoubtDto[];
  /** The server's count past the cap; shown only when it sent no more than the cap. */
  readonly pastCap: number;
  /** Carried doubts a rule settled (`doubtsSettled`). */
  readonly settled?: readonly MeetingSettledDoubtDto[];
  /** The question an answer is running for; every button is inert meanwhile. */
  readonly busyDoubtId: string | null;
  /** Absent: the card only shows (the meeting has no project yet). */
  readonly onAnswer?: (doubtId: string, answer: MeetingDoubtAnswer, leaveOut: readonly string[]) => void;
}) {
  const t = useT();
  const tn = usePlural();
  // Per question, the items the owner leaves out (topic ids).
  const [leftOut, setLeftOut] = useState<Readonly<Record<string, readonly string[]>>>({});
  const [showAll, setShowAll] = useState(false);
  if (doubts.length === 0 && pastCap === 0 && settled.length === 0) return null;
  const shown = showAll ? doubts : doubts.slice(0, MEETING_MAX_DOUBTS);
  const folded = doubts.length - shown.length;
  // A server that sends every question has none waiting elsewhere; an older
  // one sent at most five and counted the rest.
  const elsewhere = doubts.length > MEETING_MAX_DOUBTS ? 0 : pastCap;
  const anyUnfiled = doubts.some((d) => !d.items.every(isFiledItem));
  const toggle = (doubtId: string, topicId: string): void =>
    setLeftOut((prev) => {
      const now = prev[doubtId] ?? [];
      return {
        ...prev,
        [doubtId]: now.includes(topicId) ? now.filter((id) => id !== topicId) : [...now, topicId],
      };
    });
  return (
    <section className="meet-section meet-doubts" aria-label={t("meetings.doubts.title")}>
      <h2 className="meet-section-title">{t("meetings.doubts.title")}</h2>
      {anyUnfiled && <Help>{t("meetings.doubts.lead")}</Help>}
      <ul className="meet-list">
        {shown.map((d) => {
          const out = (leftOut[d.doubtId] ?? []).filter((id) => d.items.some((i) => i.topicId === id));
          const pick = onAnswer !== undefined && canLeaveOut(d);
          return (
            <li key={d.doubtId} className="meet-row meet-doubt" data-doubt={d.doubtId}>
              <p className="meet-question-text">{t(doubtQuestionKeyOf(d))}</p>
              {(d.carry ?? null) !== null && <span className="meet-meta">{t("meetings.doubts.carried")}</span>}
              {(d.earlierAnswer ?? null) !== null && (
                <p className="meet-meta" data-earlier-answer>
                  {t("meetings.doubts.earlierAnswer", { answer: t(doubtAnswerKeyOf(d, d.earlierAnswer!)) })}
                </p>
              )}
              {d.items.map((item) => (
                <div key={item.topicId} className="meet-doubt-item">
                  <div className="meet-row-head">
                    {item.summary !== null && <span className="meet-summary">{item.summary}</span>}
                    <span className="meet-meta">{t("meetings.quote.at", { clock: meetingClock(item.startMs) })}</span>
                  </div>
                  <FiledAs item={item} />
                  <HeldAs item={item} />
                  {item.quotes !== null && item.quotes.length > 0 && <QuoteList quotes={item.quotes} />}
                  {pick && (
                    <button
                      type="button"
                      className={out.includes(item.topicId) ? "focus-chip is-on" : "focus-chip"}
                      aria-pressed={out.includes(item.topicId)}
                      disabled={busyDoubtId !== null}
                      onClick={() => toggle(d.doubtId, item.topicId)}
                    >
                      {t(isFiledItem(item) ? "meetings.doubts.leaveOut.filed" : "meetings.doubts.leaveOut")}
                    </button>
                  )}
                </div>
              ))}
              {onAnswer !== undefined && (
                <div className="meet-actions">
                  {orderedAnswers(d).map((a) => (
                    <button
                      key={a}
                      type="button"
                      className={a === d.suggested ? "focus-btn" : "focus-btn is-ghost"}
                      disabled={busyDoubtId !== null || !answerOpen(a, out.length, d.items.length)}
                      onClick={() => onAnswer(d.doubtId, a, a === "as_held" ? out : [])}
                    >
                      {t(doubtAnswerKeyOf(d, a))}
                    </button>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {folded > 0 && (
        <button type="button" className="focus-deeplink" onClick={() => setShowAll(true)}>
          {tn("plural.meetingDoubtsMore", folded)}
        </button>
      )}
      {elsewhere > 0 && <p className="meet-meta">{tn("plural.meetingDoubtsPastCap", elsewhere)}</p>}
      <SettledDoubts settled={settled} />
    </section>
  );
}
